import React from 'react';
import Reveal from './Reveal';

/**
 * How it works: one week with one bell, shown with four real app images
 * (owner, 2026-10-27). Each caption says what the image shows and nothing
 * about what the reader will do. The words and numbers match the frames:
 *
 *   today      release 1.0.0 App Store frame (Mon 19 Oct, Foundations W1 D1)
 *   lock-rest  the Lock Screen Live Activity at rest (Tue 20 Oct, 16 kg Bo)
 *   crew       release 1.0.0 App Store frame (Alex's session, 💪 1 and 🔥 1)
 *   share      the week share card (WEEK 4 DONE, 3 sessions, 3 days)
 *
 * The crew caption may only name reactions in src/community/crewModel.js
 * REACTIONS (weekWithOneBell.test.js reads it). Images: public/media/week-*,
 * 480x1043 (2x the widest display), AVIF and WebP with a JPEG fallback.
 */
export const MOMENTS = [
  {
    id: 'today',
    day: 'Monday, 9:41',
    caption:
      "Today's session is already written for the bell you own: Foundations, week 1, day 1, 17 minutes.",
    alt: 'Bell Club Today screen: this week 0 of 3, and Hinge and Press, Foundations week 1 day 1, 17 minutes, 3 moves, a 12 kg bell, with a Start button.',
  },
  {
    id: 'lock-rest',
    day: 'Tuesday, between sets',
    caption: 'Phone locked, rest timer running: pause it or skip the rest without opening the app.',
    alt: 'iPhone Lock Screen with the Bell Club Live Activity: Rest, 0:35 left, up next single-arm row, with Pause and Skip rest buttons and Bo the kettlebell meditating.',
  },
  {
    id: 'crew',
    day: 'Sunday, 9:41',
    caption:
      'Alex finished a session and the crew answered with 💪 and 🔥. No leaderboard, no kilos compared.',
    alt: "Bell Club Crew screen: the crew trained 1 of 3 sessions this week, and Alex's finished session in the activity feed with a flexed arm and a fire reaction.",
  },
  {
    id: 'share',
    day: 'Sunday, later',
    caption: 'Three sessions on three days, week four done, and a card to share it.',
    alt: 'Bell Club share card: Week streak, Bo meditating, Week 4 done, 3 sessions, 3 days trained, 4210 kg weight moved.',
  },
];

const WIDTH = 480;
const HEIGHT = 1043;

const HowItWorks = () => (
  <section id="how" className="section section-ink" aria-labelledby="how-headline">
    <div className="section-inner">
      <Reveal>
        <p className="eyebrow">How it works</p>
        <h2 id="how-headline" className="h2 h2-narrow">
          A week with one bell.
        </h2>
        <p className="week-sub">What Bell Club looks like between Monday and Sunday.</p>
      </Reveal>
      {/* On a phone the row scrolls sideways; a scrollable region has to be
          reachable from the keyboard, so it takes focus and a name. */}
      <div className="week-scroll" role="region" aria-label="One week, four moments" tabIndex={0}>
        <ol className="week">
          {MOMENTS.map(({ id, day, caption, alt }) => (
            <li key={id} className="week-moment">
              <h3 className="week-day">{day}</h3>
              <picture>
                <source type="image/avif" srcSet={`/media/week-${id}.avif`} />
                <source type="image/webp" srcSet={`/media/week-${id}.webp`} />
                <img
                  className="week-img"
                  src={`/media/week-${id}.jpg`}
                  alt={alt}
                  width={WIDTH}
                  height={HEIGHT}
                  loading="lazy"
                  decoding="async"
                />
              </picture>
              <p className="week-caption">{caption}</p>
            </li>
          ))}
        </ol>
      </div>
    </div>
  </section>
);

export default HowItWorks;
